import { Button, Modal as BootstrapModal } from 'react-bootstrap';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Toggle from "../../../../controls/Toggle";
import GenericDropdown from '../../../../controls/GenericDropdown';
import ListFilter from '../ListFilter';
import { useUIConfig } from '../Context';
import BUILD_TIME_CONFIG_TYPES from '../../../../../config/buildTimeConfigTypes';

const allowedPathAsLayoutItem = [BUILD_TIME_CONFIG_TYPES.BLOCKS, BUILD_TIME_CONFIG_TYPES.APPLETS];
const CreateLayoutItemModal = ({
    show,
    onCreate,
    onCancel,
    defaultValue,
    layoutItemType,
    preselectedCopyItem
}) => {
    const { jsxComponents, components } = useUIConfig();
    const [translate] = useTranslation();
    const [newLayoutItem, setNewLayoutItem] = useState({ name: '', path: '' });
    const [existingLayoutItem, setExistingLayoutItem] = useState(defaultValue || '');
    const [layoutItemName, setLayoutItemName] = useState('');
    const [createFromExisting, setCreateFromExisting] = useState(preselectedCopyItem ? true : false);

    const addButtonDisabled = useCallback(() => {
        if (createFromExisting) {
            return existingLayoutItem === '' || layoutItemName === '';
        }

        return layoutItemName === '' || newLayoutItem.path === '' || (newLayoutItem.name === '' && newLayoutItem.path === "LayoutItem");
    }, [newLayoutItem, existingLayoutItem, createFromExisting, layoutItemName]);

    const getDisplayLabelName = useCallback(() => {
        switch (layoutItemType) {
            case BUILD_TIME_CONFIG_TYPES.APPLETS:
                return "Applet's " + translate("OTM_NAME");
            case BUILD_TIME_CONFIG_TYPES.BLOCKS:
                return "Block's " + translate("OTM_NAME");
            case BUILD_TIME_CONFIG_TYPES.COMPONENTS:
            default:
                return "Component's " + translate("OTM_NAME");
        }
    }, [layoutItemType, translate]);

    const getFilterExcludedOptions = useCallback(() => {
        if (layoutItemType === BUILD_TIME_CONFIG_TYPES.COMPONENTS) {
            return ['LayoutItem'];
        }
        return [];
    }, [layoutItemType]);

    const defaultValueAllowed = useMemo(() => {
        return getFilterExcludedOptions().includes(defaultValue);
    }, [defaultValue, getFilterExcludedOptions]);

    const dropdownOptions = useMemo(() => {
        return jsxComponents.filter(({ name }) => !getFilterExcludedOptions().includes(name)).map(({ name }) => ({
            value: name,
        }));
    }, [jsxComponents, getFilterExcludedOptions]);

    useEffect(() => {
        if (show) {
            setLayoutItemName('');
            setExistingLayoutItem(preselectedCopyItem ? preselectedCopyItem : '');
            if (defaultValueAllowed) {
                setNewLayoutItem({ path: '' });
            }
            else {
                setNewLayoutItem({ path: defaultValue || '' });
            }
            setCreateFromExisting(preselectedCopyItem ? true : false);
        }
    }, [show, defaultValue, preselectedCopyItem, defaultValueAllowed]);

    const renderExistingLayoutPicker = useMemo(() => {
        if (preselectedCopyItem) {
            return <div>
                <label className='d-block form-label h6'>{translate("OTM_SELECTED_ITEM_FOR_COPYING")}</label>
                {preselectedCopyItem}
            </div>
        }
        return (
            <div className='w-100' style={{ height: 'calc(100% - 110px)' }}>
                <label className='d-block form-label h6'>{translate("OTM_SELECT_FROM_EXISITNG") + layoutItemType.split(0, -1)}</label>
                <div className='pe-2 h-100' style={{ overflowY: 'auto' }}>
                    <ListFilter
                        type={layoutItemType}
                        isTopLevel={false}
                        createNew={false}
                        defaultValue={defaultValue}
                        onSelect={(layoutItem) => setExistingLayoutItem(layoutItem)}
                    />
                </div>
            </div>
        );
    }, [layoutItemType, defaultValue, preselectedCopyItem, translate]);

    const newLayoutItemProperties = useMemo(() =>
        <>
            <GenericDropdown
                options={dropdownOptions}
                addEmptyOption={true}
                defaultValue={defaultValueAllowed ? '' : defaultValue}
                heading={translate('OTM_PATH')}
                size="sm"
                onSelect={(path) => setNewLayoutItem(prev => ({ ...prev, path }))}
                css={{ dropdown: 'mt-1 mb-2' }}
            />

            {
                allowedPathAsLayoutItem.includes(layoutItemType) && newLayoutItem.path === 'LayoutItem' &&
                <>
                    <GenericDropdown
                        options={Object.keys(components).map((name) => ({ value: name }))}
                        addEmptyOption={true}
                        heading={translate('OTM_COMPONENT_NAME')}
                        onSelect={(name) => setNewLayoutItem((prev) => ({ ...prev, name }))}
                        css={{ dropdown: 'mt-1' }}
                    />
                </>
            }
        </>
        , [layoutItemType, newLayoutItem, dropdownOptions, components, defaultValue, defaultValueAllowed, translate]);

    const renderForm = useMemo(() => {
        return (
            <>
                <BootstrapModal.Header className='h5 text-secondary'>
                    {translate("OTM_CREATE_NEW")} ({layoutItemType})
                </BootstrapModal.Header>
                <BootstrapModal.Body className="overflow-hidden d-flex flex-column" style={{ height: 'calc(100vh / 1.35)' }}>
                    <div className="row mb-2">
                        <label htmlFor="setCreateFromExisting" className="d-block form-label h6 col-auto">{translate('OTM_CREATE_FROM_EXISTING')}</label>
                        <div className="form-check form-switch col-auto">
                            <Toggle
                                id="setCreateFromExisting"
                                onChange={() => { setCreateFromExisting(prev => !prev); setExistingLayoutItem(preselectedCopyItem ? preselectedCopyItem : '') }}
                                defaultState={createFromExisting}
                            />
                        </div>
                    </div>

                    <label className='d-block form-label h6'>{getDisplayLabelName()}</label>
                    <input
                        type='text'
                        className="form-control form-control-sm mb-2"
                        value={layoutItemName}
                        onInput={(event) => setLayoutItemName(event.target.value)}
                    />

                    {createFromExisting ? renderExistingLayoutPicker : newLayoutItemProperties}

                </BootstrapModal.Body>
                <BootstrapModal.Footer>
                    <Button disabled={addButtonDisabled()} onClick={() => onCreate({ layoutItemName, layoutItem: existingLayoutItem || newLayoutItem, fromExisting: createFromExisting })} className='btn-sm'>{translate('OTM_ADD')}</Button>
                    <Button onClick={onCancel} variant='secondary' className='btn-sm'>{translate('OTM_CANCEL')}</Button>
                </BootstrapModal.Footer>
            </>
        );
    }, [layoutItemType, onCancel, onCreate, preselectedCopyItem, newLayoutItemProperties, createFromExisting, newLayoutItem, layoutItemName, existingLayoutItem, renderExistingLayoutPicker, translate, getDisplayLabelName, addButtonDisabled]);

    return (
        <BootstrapModal size='lg' show={show} backdrop='static'>
            {renderForm}
        </BootstrapModal>
    );
};

export default CreateLayoutItemModal;